import { ChevronRight, Info, MessageSquareText, Sparkles, TriangleAlert } from 'lucide-react';
import { AskLognAIIcon } from '@lognai/plugin-ai-core-ui';
import { Badge, Button } from '@lognai/synapse-design-system-v2';
import type { EvidenceFact, InsightSpec, SemanticQueryTrace } from '@lognai/plugin-analytics';
import { businessLabel } from '../lib/user-facing';

interface Props {
  insight: InsightSpec;
  evidence: EvidenceFact[];
  trace: SemanticQueryTrace[];
  onSuggestedQuestion?: (question: string) => void;
  disabled?: boolean;
  source?: 'ai' | 'calculated' | 'manual';
  /**
   * Drops the headline row when the host already shows it. Headlines are phrased
   * for a panel that opens away from its chart; embedded in a card that names
   * the chart twice over, the row is pure repetition.
   */
  hideHeadline?: boolean;
}

const formatEvidenceValue = (fact: EvidenceFact): string => {
  if (typeof fact.value !== 'number') return fact.value;
  if (fact.unit === 'currency') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1,
    }).format(fact.value);
  }
  if (fact.unit === 'percent') return `${fact.value.toFixed(1)}%`;
  if (fact.unit === 'days') return `${fact.value.toFixed(1)} days`;
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(fact.value);
};

export function InsightPanel({ insight, evidence, trace, onSuggestedQuestion, disabled = false, source, hideHeadline = false }: Props) {
  const evidenceById = new Map(evidence.map((fact) => [fact.id, fact]));
  const resolvedSource = source ?? (insight.fallback ? 'calculated' : 'ai');
  const sourceLabel = resolvedSource === 'manual' ? 'Team note' : resolvedSource === 'calculated' ? 'Calculated insight' : 'AI insight';
  return (
    <section className="border-t border-border bg-muted/10 px-4 py-4" aria-label={resolvedSource === 'manual' ? 'Team note' : 'AI explanation'}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary-subtle text-primary">{resolvedSource === 'manual' ? <MessageSquareText size={15} aria-hidden="true" /> : <AskLognAIIcon size={15} aria-hidden="true" />}</span>
          {!hideHeadline && (
            <h3 className="break-words text-sm font-semibold text-foreground">{insight.headline}</h3>
          )}
        </div>
        <Badge variant="outline">{sourceLabel}</Badge>
      </div>

      <p className="mt-3 text-sm leading-6 text-muted-foreground">{insight.summary}</p>

      {insight.findings.length > 0 && (
        <div className="mt-4 space-y-3">
          {insight.findings.map((finding, index) => {
            const citedFacts = finding.evidenceIds.map((id) => evidenceById.get(id)).filter((fact): fact is EvidenceFact => Boolean(fact));
            return (
              <div key={`${index}-${finding.text.slice(0, 24)}`} className="border-l-2 border-primary pl-3">
                <p className="text-sm leading-5 text-foreground">{finding.text}</p>
                {citedFacts.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {citedFacts.map((fact) => (
                      <span key={fact.id} className="rounded-md border border-border bg-surface px-2 py-1 text-xs text-muted-foreground" title={fact.context}>
                        {fact.label}: <strong className="font-semibold text-foreground">{formatEvidenceValue(fact)}</strong>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {insight.caveats.length > 0 && (
        <div className="mt-4 flex gap-2 rounded-lg border border-warning-border bg-warning-subtle px-3 py-2 text-xs leading-5 text-warning-foreground">
          <TriangleAlert className="mt-0.5 shrink-0" size={14} />
          <div>{insight.caveats.map((caveat) => <p key={caveat}>{caveat}</p>)}</div>
        </div>
      )}

      {insight.suggestedQuestions.length > 0 && (
        <div className="mt-4">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase text-muted-foreground"><Sparkles size={12} /> Follow up</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {insight.suggestedQuestions.map((suggestion) => (
              <Button key={suggestion} variant="ghost" size="xs" disabled={disabled} onClick={() => onSuggestedQuestion?.(suggestion)}>
                <span className="max-w-full whitespace-normal text-left">{suggestion}</span><ChevronRight size={12} />
              </Button>
            ))}
          </div>
        </div>
      )}

      <details className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
        <summary className="flex cursor-pointer list-none items-center gap-2 font-medium text-foreground">
          <Info size={13} /> {resolvedSource === 'manual' ? 'About this note' : 'About this insight'}
          <span className="font-normal text-muted-foreground">Based on current business data</span>
        </summary>
        <div className="mt-3 space-y-2">
          {trace.map((item, index) => (
            <div key={`${item.view}-${index}`} className="rounded-lg border border-border bg-surface p-3">
              <div className="flex flex-wrap justify-between gap-2"><strong className="text-foreground">{businessLabel(item.view)}</strong><span>{item.rowCount} records considered</span></div>
              <p className="mt-2 break-words">Values analyzed: {item.measures.map(businessLabel).join(', ') || 'None'}</p>
              <p className="mt-1 break-words">Grouped by: {item.dimensions.map(businessLabel).join(', ') || 'None'}</p>
              {item.filters.length > 0 && <p className="mt-1 break-words">Filters: {item.filters.map((filter) => `${businessLabel(filter.member)} ${businessLabel(filter.operator)} ${filter.values?.join(', ') ?? ''}`).join('; ')}</p>}
            </div>
          ))}
        </div>
      </details>
    </section>
  );
}
